/*!
 * Created by netlogix GmbH & Co. KG
 *
 * @copyright netlogix GmbH & Co. KG
 */

import { SelectionModel } from '@angular/cdk/collections';
import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { State } from '@core/store';
import { Store } from '@ngrx/store';
import { Tour, UserRole } from '@shared/models';
import { TourViewMode } from '@shared/models/tour-view-mode';
import { ObjectIdPipe } from '@shared/pipes/object-id.pipe';
import { TourStateDefinition } from '@tours/models/tour-state-definition';
import { TourStatesService } from '@tours/services/tour-states.service';
import * as TourActions from '@tours/store/tours.actions';
import { getTourViewModeStateFilter, isSidebarExpanded } from '@tours/store/tours.selectors';
import { NgxPermissionsService } from 'ngx-permissions';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
    selector: 'app-tour-list',
    templateUrl: './tour-list.component.html',
    styleUrls: ['./tour-list.component.scss'],
    standalone: false
})
export class TourListComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() tours: Tour[];
  viewMode = TourViewMode;
  @Input() mode: TourViewMode = TourViewMode.TourList;

  @ViewChild(MatSort, { static: true }) matSort?: MatSort;

  dataSource: MatTableDataSource<Tour> = new MatTableDataSource([] as Tour[]);

  stateFilterValues = [];
  tableColumns = [];
  selectedTours = new SelectionModel<Tour>(true, []);
  isSidebarExpanded$: Observable<boolean>;
  tourStates: TourStateDefinition[];

  private currentStateFilterValueSubscription: Subscription;

  constructor(
    private store: Store<State>,
    private tourStatesService: TourStatesService,
    private router: Router,
    private objectIdPipe: ObjectIdPipe,
    private activatedRoute: ActivatedRoute,
    private permissionsService: NgxPermissionsService,
  ) {}

  ngOnInit(): void {
    this.isSidebarExpanded$ = this.store.select(isSidebarExpanded);
    this.tourStates = this.tourStatesService.getStateDefinitions();

    this.currentStateFilterValueSubscription = this.store.select(getTourViewModeStateFilter, {tourViewMode: this.mode}).pipe(
      tap(values => this.stateFilterValues = values)
    ).subscribe();

    switch (this.mode) {
      case TourViewMode.MonthOverview:
        this.tableColumns = [
          'type',
          'remarks',
          'costCenter',
          'appointmentClass',
          'time',
          'date',
          'planDate',
          'state',
          'action',
          'select'
        ];
        break;
      case TourViewMode.ReturnList:
        this.tableColumns = [
          'type',
          'constructionSite',
          'customer',
          'remarks',
          'drivers',
          'vehicles',
          'planDate',
          'time',
          'appointmentClass',
          'manager',
          'state',
          'action',
          'select'
        ];
        break;
      default:
        this.tableColumns = [
          'type',
          'constructionSite',
          'customer',
          'remarks',
          'vao',
          'drivers',
          'vehicles',
          'planDate',
          'time',
          'appointmentClass',
          'manager',
          'state',
          'action',
          'select'
        ];
    }

    this.permissionsService.hasPermission(UserRole.User).then(canWrite => {
      if (false === canWrite) {
        this.tableColumns = this.tableColumns.filter(column => 'select' !== column);
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.matSort) {
      return;
    }

    this.matSort.sortChange.subscribe(sorting => {
      this.safeSorting(sorting);
    });
  }

  safeSorting(sorting: Sort): void {
    localStorage.setItem('sorting', JSON.stringify(sorting));
  }

  loadSorting(): Sort {
    return JSON.parse(localStorage.getItem('sorting')) || { active: '', direction: 'desc' } as Sort;
  }

  ngOnChanges(): void {
    const newDatasource = new MatTableDataSource(this.tours);
    newDatasource.filterPredicate = this.filterStateValues.bind(this);
    const sorting = this.loadSorting();

    if (this.matSort) {
      newDatasource.sort = Object.assign(this.matSort, sorting);
    }

    newDatasource.sortingDataAccessor = (tour, property) => {
      switch (property) {
        case 'tour.constructionSite.name': return tour.constructionSite.name;
        case 'tour.customer.code': return tour.customer.code;
        case 'tour.manager.shortHand': return tour.manager.shortHand;
        default: return tour[property];
      }
    };
    newDatasource.filter = 'enable'; // just some text to always trigger filter function
    this.dataSource = newDatasource;

    if (newDatasource.sort) {
      newDatasource.sort.sortChange.emit(sorting);
    }
  }

  filterStateValues(tour: Tour, filter: string): boolean {
    if (this.stateFilterValues.length === 0) {
      return true;
    }

    return this.stateFilterValues.indexOf(tour.state) !== -1;
  }

  ngOnDestroy(): void {
    if (this.currentStateFilterValueSubscription) {
      this.currentStateFilterValueSubscription.unsubscribe();
    }
  }

  get areAllToursSelected(): boolean {
    return !!this.tours?.length && this.selectedTours.selected.length === this.selectableTours.length;
  }

  get selectableTours(): Tour[] {
    return this.dataSource.filteredData.filter(tour => tour.allowPostpone);
  }

  toggleMasterSelection(): void {
    if (this.areAllToursSelected) {
      this.selectableTours.forEach(tour => this.selectedTours.deselect(tour));
    } else {
      this.selectableTours.forEach(tour => this.selectedTours.select(tour));
    }
  }

  postponeTours(): void {
    const toursToPostpone = this.selectedTours.selected.map(tour => tour['@id']);
    this.store.dispatch(new TourActions.PostponeTours(toursToPostpone));
    this.selectedTours.clear();
  }

  onRowClick(tour: Tour): void {
    this.router.navigate(['../tours', this.objectIdPipe.transform(tour), 'edit'], { relativeTo: this.activatedRoute });
  }

  onStateFilterChange(stateFilterValues: any): void {
    this.store.dispatch(new TourActions.SetStateFilter({
      tourViewMode: this.mode,
      stateFilterValues
    }));
  }
}
